import React, { Fragment } from "react";
import { ILaserBoardWithMouseTraceProps, ILaserBoardProps, ILaserContainerProps, ILaserProps, TPoint, TLaser } from "./interface";
import { useMouseTrace } from "./hooks/useMouseTrace";

const DEFAULT_CIRCLE_SIZE = 10;

export const Laser: React.FC<ILaserProps> = (props) => {
    const { currentLaser, prevLaser, currentIndex, listLength } = props;
    const startOpacity = 1 - (currentIndex / listLength);
    const targetOpacity = 1 - ((currentIndex + 1) / listLength);
    if (isNaN(currentLaser.edglePoint1.X) || (prevLaser && isNaN(prevLaser.edglePoint1.X))) {
        return null;
    }
    return prevLaser
        ? <Fragment>
            <linearGradient gradientUnits="userSpaceOnUse" x1={currentLaser.X} y1={currentLaser.Y} x2={prevLaser.X} y2={prevLaser.Y} id={currentLaser.id}>
                <stop offset="0%" stopColor="#FF273D" stopOpacity={targetOpacity}>
                </stop>
                <stop offset="100%" stopColor="#FF273D" stopOpacity={startOpacity}></stop>
            </linearGradient>
            <path
                fill={`url(#${currentLaser.id})`}
                strokeLinecap="butt"
                strokeMiterlimit="0"
                d={`M ${currentLaser.edglePoint1.X} ${currentLaser.edglePoint1.Y} L ${currentLaser.edglePoint2.X} ${currentLaser.edglePoint2.Y} ${prevLaser.edglePoint2.X} ${prevLaser.edglePoint2.Y} ${prevLaser.edglePoint1.X} ${prevLaser.edglePoint1.Y} Z`}>
            </path>
        </Fragment>
        : <circle cx={currentLaser.X} cy={currentLaser.Y} r={currentLaser.R} fill="#FF273D" strokeWidth="0"></circle>
}

export const LaserBoardWithMouseTrace: React.FC<ILaserBoardWithMouseTraceProps> = (props) => {
    const { width, height, circleSize = DEFAULT_CIRCLE_SIZE } = props;
    const pointList = useMouseTrace();
    return (
        <LaserBoard width={width} height={height} pointList={pointList} circleSize={circleSize} />
    );
}

export const LaserContainer: React.FC<ILaserContainerProps> = React.memo((props) => {
    const getTwoEdglePoints = (currentPoint: TPoint, nextPoint: TPoint, R: number) => {
        const defaultResult = {
            edglePoint1: {
                X: currentPoint.X,
                Y: currentPoint.Y
            },
            edglePoint2: {
                X: currentPoint.X,
                Y: currentPoint.Y
            },
        };
        if (!nextPoint) {
            return defaultResult;
        } 
        const point = nextPoint;
        const deltaX = point.X - currentPoint.X;
        const deltaY = point.Y - currentPoint.Y;
        const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
        if (distance === 0) {
            return defaultResult;
        }
        const normalizedDeltaX = deltaX / distance;
        const normalizedDeltaY = deltaY / distance;

        return {
            edglePoint1: {
                X: currentPoint.X + normalizedDeltaY * R,
                Y: currentPoint.Y - normalizedDeltaX * R,
            },
            edglePoint2: {
                X: currentPoint.X - normalizedDeltaY * R,
                Y: currentPoint.Y + normalizedDeltaX * R,
            },
        }
    }

    const getLaserList = (pointList: TPoint[], circleSize: number): TLaser[] => {
        const laserList = pointList.map((currentPoint, index) => {
            const nextPoint = pointList[index + 1];
            const R = (circleSize - index * (circleSize / pointList.length)) / 2;
            const { edglePoint1, edglePoint2 } = getTwoEdglePoints(currentPoint, nextPoint, R);
            return {
                ...currentPoint,
                edglePoint1,
                edglePoint2,
                R,
                id: Math.random().toString().substr(2, 10) + Date.now().toString(36)
            }
        });
        return laserList;
    }

    const { pointList, circleSize = DEFAULT_CIRCLE_SIZE } = props;
    const laserList: TLaser[] = getLaserList(pointList, circleSize);
    return <Fragment>
        {
            laserList.map((currentLaser, index) => <Laser
                currentLaser={currentLaser}
                currentIndex={index}
                prevLaser={laserList[index - 1]}
                listLength={laserList.length}
                key={currentLaser.id}
            />)
        }
    </Fragment>
}, (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next);
})

export const LaserBoard: React.FC<ILaserBoardProps> = (props) => {
    const { width, height, pointList, circleSize } = props;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            fillRule="evenodd"
            fill="none"
            stroke="none"
            strokeLinecap="square"
            strokeMiterlimit="10"
            overflow="hidden"
            preserveAspectRatio="none"
            pointerEvents="none"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ lineHeight: "normal", cursor: 'none' }}
        >
            <LaserContainer pointList={pointList} circleSize={circleSize} />
        </svg>
    )
}
